import { useState, useCallback } from 'react';
import { contentAPI, ChunkedUploader } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

export const useUpload = (onClose, onUploadComplete) => {
  const [files, setFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('single');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [subscriptionError, setSubscriptionError] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    tags: '',
    visibility: 'public'
  });

  const [scheduleState, setScheduleState] = useState({
    enabled: false,
    date: '',
    time: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  const resetState = useCallback(() => {
    setFiles([]);
    setFormData({ title: '', description: '', genre: '', tags: '', visibility: 'public' });
    setUploadProgress({});
    setScheduleState({
      enabled: false,
      date: '',
      time: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    setSubscriptionError(null);
  }, []);

  const handleSubscriptionError = useCallback((error) => {
    const errorDetail = error.response?.data?.detail;
    if (errorDetail?.error === 'Subscription required' || error.response?.status === 403) {
      setSubscriptionError({
        message: errorDetail?.message || "Upgrade your plan to upload content",
        currentPlan: errorDetail?.current_plan || 'basic',
        upgradeUrl: errorDetail?.upgrade_url || '/pricing'
      });
      return true;
    }
    return false;
  }, []);

  const handleFileSelect = useCallback((event) => {
    const selectedFiles = Array.from(event.target.files);
    
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('video/') || 
                          file.type.startsWith('audio/') || 
                          file.type.startsWith('image/');
      const isValidSize = file.size <= 500 * 1024 * 1024;
      
      if (!isValidType) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: `${file.name} is not a valid media file`,
        });
        return false;
      }
      
      if (!isValidSize) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: `${file.name} exceeds 500MB limit`,
        });
        return false;
      }
      
      return true;
    });

    setFiles(validFiles);
  }, [toast]);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadSingleFile = useCallback(async (file) => {
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('title', formData.title || file.name);
      data.append('description', formData.description || '');
      data.append('genre', formData.genre || '');
      data.append('tags', formData.tags || '[]');
      data.append('visibility', formData.visibility);
      
      if (scheduleState.enabled && scheduleState.date && scheduleState.time) {
        const scheduledDateTime = new Date(`${scheduleState.date}T${scheduleState.time}`);
        data.append('scheduled_at', scheduledDateTime.toISOString());
        data.append('schedule_timezone', scheduleState.timezone);
        data.append('is_scheduled', 'true');
      }

      const response = await contentAPI.uploadFile(data, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Upload error:', error);
      if (handleSubscriptionError(error)) {
        return { success: false, error: 'subscription_required', isSubscriptionError: true };
      }
      return { success: false, error: error.response?.data?.detail || 'Upload failed' };
    }
  }, [formData, scheduleState, handleSubscriptionError]);

  const uploadChunkedFile = useCallback(async (file) => {
    try {
      const uploader = new ChunkedUploader(file, {
        onProgress: (progress) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: Math.round(progress) }));
        },
        onError: (error) => {
          console.error('Chunked upload error:', error);
        }
      });

      const metadata = {
        title: formData.title || file.name,
        description: formData.description || '',
        genre: formData.genre || '',
        tags: formData.tags || '[]',
        visibility: formData.visibility
      };
      
      if (scheduleState.enabled && scheduleState.date && scheduleState.time) {
        const scheduledDateTime = new Date(`${scheduleState.date}T${scheduleState.time}`);
        metadata.scheduled_at = scheduledDateTime.toISOString();
        metadata.schedule_timezone = scheduleState.timezone;
        metadata.is_scheduled = true;
      }

      const result = await uploader.start(metadata);
      return { success: true, data: result };
    } catch (error) {
      if (error.message?.includes('403') || error.message?.includes('Subscription')) {
        setSubscriptionError({
          message: "Upgrade your plan to upload content",
          currentPlan: 'basic',
          upgradeUrl: '/pricing'
        });
        return { success: false, error: 'subscription_required', isSubscriptionError: true };
      }
      return { success: false, error: error.message || 'Chunked upload failed' };
    }
  }, [formData, scheduleState]);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No files selected',
        description: 'Please select at least one file to upload',
      });
      return;
    }

    setUploading(true);
    const results = [];

    for (const file of files) {
      if (subscriptionError) break;
      
      const fileSizeMB = file.size / (1024 * 1024);
      let result;

      if (fileSizeMB > 10) {
        result = await uploadChunkedFile(file);
      } else {
        result = await uploadSingleFile(file);
      }

      if (result.isSubscriptionError) {
        setUploading(false);
        return;
      }

      results.push({ file: file.name, ...result });
    }

    setUploading(false);

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      const isScheduled = scheduleState.enabled && scheduleState.date && scheduleState.time;
      toast({
        title: isScheduled ? 'Content scheduled!' : 'Upload complete!',
        description: isScheduled 
          ? `Successfully scheduled ${successCount} file(s) for ${scheduleState.date} at ${scheduleState.time}`
          : `Successfully uploaded ${successCount} file(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
      });
      
      if (onUploadComplete) {
        onUploadComplete(results);
      }

      resetState();
      onClose();
    } else if (results.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'All uploads failed. Please try again.',
      });
    }
  }, [files, subscriptionError, scheduleState, uploadSingleFile, uploadChunkedFile, toast, onUploadComplete, onClose, resetState]);

  return {
    files,
    uploadMode,
    setUploadMode,
    uploading,
    uploadProgress,
    subscriptionError,
    setSubscriptionError,
    formData,
    setFormData,
    scheduleState,
    setScheduleState,
    handleFileSelect,
    removeFile,
    handleUpload,
    resetState
  };
};
